import { IResumoDoLoteTaxCurrentAccount } from "../../_services/interface/taxCurrentAccount.interface";

/**
 * GEN-3984 — as consultas da Conta Corrente (catálogo TAX_CURRENT_ACCOUNT). Cada uma é uma
 * sub-página do módulo e tem a própria flag de produto no cliente (`products[slug]`): uma não é
 * pré-requisito da outra.
 */
export const TAX_STATUS_SLUG = "tax-status";
export const CADIN_FEDERAL_SLUG = "cadin_federal";

export const CONTA_CORRENTE_SUBPAGINAS = [
  // a Situação Fiscal lista também lançamentos manuais estaduais/municipais: qualquer jurisdição vê
  { slug: TAX_STATUS_SLUG, path: "situacao-fiscal", label: "Situação Fiscal", exigeFederal: false },
  // o CADIN Federal é só federal (o backend recusa a consulta e o lote a quem não vê a federal)
  { slug: CADIN_FEDERAL_SLUG, path: "cadin-federal", label: "CADIN Federal", exigeFederal: true },
] as const;

/**
 * As sub-páginas que o cliente contratou, na ordem do menu. Com `jurisdicoesVisiveis` (a régua de
 * `getVisibleJurisdictions`), a sub-página só federal some para quem não vê a jurisdição federal.
 * Sem o argumento, não recorta por jurisdição.
 */
export const subpaginasDoCliente = (
  products: Record<string, unknown> | null | undefined,
  jurisdicoesVisiveis?: ReadonlyArray<string>
) =>
  CONTA_CORRENTE_SUBPAGINAS.filter(
    (subpagina) =>
      !!products?.[subpagina.slug] &&
      (!subpagina.exigeFederal || !jurisdicoesVisiveis || jurisdicoesVisiveis.includes("federal"))
  );

/**
 * GEN-3984 — por que "Atualizar consulta"/"Nova consulta" não começaria numa linha, ou `undefined` quando
 * pode: sem permissão vem primeiro; depois o pré-requisito que o backend informa na linha do CADIN
 * (`motivoSemConsulta`). Linha sem o campo (Situação Fiscal, backend anterior) não bloqueia.
 */
export const motivoSemNovaConsulta = (
  temPermissao: boolean,
  semPermissao: string,
  linha: { motivoSemConsulta?: string | null } | null | undefined
): string | undefined => (!temPermissao ? semPermissao : linha?.motivoSemConsulta || undefined);

/**
 * GEN-3984 — a linha que o drawer mostra, na versão da lista ATUAL (a recarga silenciosa traz o motivo
 * novo); a cópia do clique só vale se a linha saiu da página.
 */
export const linhaAtualDaLista = <T extends { guid_tax_current_account_list: string }>(
  copiaDoClique: T | null,
  lista: ReadonlyArray<T> | null | undefined
): T | null =>
  copiaDoClique
    ? lista?.find((l) => l.guid_tax_current_account_list === copiaDoClique.guid_tax_current_account_list) ??
      copiaDoClique
    : null;

/** A sub-página de uma consulta (slug do catálogo); `undefined` se o slug não tem tela no módulo. */
export const subpaginaDoSlug = (slug: string | null | undefined) =>
  CONTA_CORRENTE_SUBPAGINAS.find((subpagina) => subpagina.slug === slug);

/** O aviso `refreshTaxCurrentAccount` do backend: consulta individual concluída. */
export interface IAvisoDeConsultaConcluida {
  type?: string;
  /** a consulta (slug do catálogo); ausente = backend anterior à GEN-3984, que só tinha a Situação Fiscal */
  slug?: string | null;
  guid_company?: string | null;
  /**
   * o status GRAVADO pela consulta (`pendency` | `not-pendency` | `error`); ausente/null = backend
   * anterior, que não mandava
   */
  slug_status?: string | null;
}

/**
 * GEN-3984 — se o aviso de concluída diz como a consulta terminou: `true` = erro, `false` = gravou
 * resultado, `null` = o aviso não diz (backend anterior).
 */
export const avisoTerminouComErro = (message: IAvisoDeConsultaConcluida | null | undefined): boolean | null =>
  message?.slug_status ? message.slug_status === "error" : null;

/**
 * GEN-3984 — se a linha oferece "Exportar" (o Excel da empresa, abas Empresas + Registros). Só o CADIN
 * Federal: a Situação Fiscal já tem "Visualizar relatório" e "Planilha de débitos".
 */
export const linhaTemExportar = (slug: string | null | undefined): boolean => slug === CADIN_FEDERAL_SLUG;

/** GEN-3984 — chave de uma consulta individual disparada: a consulta (slug) e a empresa. */
export const chaveDaConsulta = (slug: string, guid_company: string) => `${slug}:${guid_company}`;

/**
 * GEN-3984 — id do toast "Pesquisando… em segundo plano" de uma consulta. Estável por
 * consulta + empresa para o painel fechá-lo pelo aviso de concluída mesmo depois de remontar (troca de
 * sub-página, de cliente, saída e volta) — quando já não sabe que foi ele quem disparou.
 */
export const idToastConsultaEmSegundoPlano = (slug: string, guid_company: string) =>
  `tca-bg:${chaveDaConsulta(slug, guid_company)}`;

/**
 * GEN-3984 — por quanto tempo uma consulta disparada pela tela espera o aviso de concluída. Depois disso
 * a pendência é descartada: o aviso pode nunca chegar (falha sem evento, backend reiniciado) e uma
 * pendência eterna faria um aviso de horas depois (recorrência, lote) parecer da consulta da tela.
 */
export const PRAZO_CONSULTA_PENDENTE_MS = 30 * 60 * 1000;

/** GEN-3984 — se a pendência registrada em `desde` (ms) já passou do prazo em `agora`. */
export const pendenciaVencida = (desde: number, agora: number) => agora - desde > PRAZO_CONSULTA_PENDENTE_MS;

/**
 * GEN-3984 — status da fila (`updateCertificateSearchModal`, `checkSlugRunning`) no vocabulário das
 * telas: o Databroker manda `FAIL`, as telas reconhecem `FAILED`. O sufixo (`RETRY.2`) é preservado.
 */
export const normalizarStatusDaFila = (status: string | null | undefined): string => {
  const bruto = String(status ?? "");
  const [prefixo, ...resto] = bruto.split(".");
  return prefixo === "FAIL" ? ["FAILED", ...resto].join(".") : bruto;
};

/** GEN-3984 — a consulta (`chaveDaConsulta`) que a fila diz ter falhado, ou `null`. */
export const chaveDaFalhaNaFila = (
  msgData: { slug?: string | null; guid_company?: string | null; status?: string | null } | null | undefined
): string | null => {
  if (!msgData?.slug || !msgData.guid_company) return null;
  if (normalizarStatusDaFila(msgData.status).split(".")[0] !== "FAILED") return null;
  return chaveDaConsulta(msgData.slug, msgData.guid_company);
};

/**
 * GEN-3984 — a consulta pendente desta sub-página a que o aviso do backend se refere (a chave dela em
 * `pendentes`), ou `null` se a tela não deve reagir.
 *
 * Só reage a consulta que ESTA tela disparou, ainda não foi avisada e está no prazo (`pendentes`: chave
 * `chaveDaConsulta` → quando foi disparada) — recorrência, lote e as irmãs da raiz do CADIN também chegam
 * por este evento.
 *
 * - Aviso com empresa: a consulta `slug:empresa` tem de estar pendente e ser da sub-página (sem slug =
 *   Situação Fiscal).
 * - Aviso sem slug E sem empresa: payload do backend anterior à GEN-3984 (`{ type, message }`), que só
 *   tinha a Situação Fiscal. Vale para ela, para a consulta pendente mais antiga, como valia antes — sem
 *   isso, front novo com backend antigo perderia o toast de concluída.
 * - Aviso com slug e sem empresa não é atribuível: não reage.
 */
export const consultaPendenteDoAviso = (
  message: IAvisoDeConsultaConcluida | null | undefined,
  { slug, pendentes, agora = Date.now() }: { slug: string; pendentes: ReadonlyMap<string, number>; agora?: number }
): string | null => {
  if (!message || message.type !== "refreshTaxCurrentAccount") return null;
  const vigentes = Array.from(pendentes.entries())
    .filter(([, desde]) => !pendenciaVencida(desde, agora))
    .sort(([, a], [, b]) => a - b)
    .map(([chave]) => chave);
  if (!message.slug && !message.guid_company) {
    if (slug !== TAX_STATUS_SLUG) return null;
    return vigentes.find((chave) => chave.startsWith(`${TAX_STATUS_SLUG}:`)) ?? null;
  }
  if (!message.guid_company) return null;
  const slugDoAviso = message.slug ?? TAX_STATUS_SLUG;
  if (slugDoAviso !== slug) return null;
  const chave = chaveDaConsulta(slugDoAviso, message.guid_company);
  return vigentes.includes(chave) ? chave : null;
};

/** GEN-3984 — se a sub-página deve avisar "pesquisa concluída" (ver `consultaPendenteDoAviso`). */
export const deveReagirAoAvisoDeConsulta = (
  message: IAvisoDeConsultaConcluida | null | undefined,
  opts: { slug: string; pendentes: ReadonlyMap<string, number>; agora?: number }
): boolean => consultaPendenteDoAviso(message, opts) !== null;

/** GEN-3984 — o que a tela sabe de uma consulta que ela disparou e ainda não foi avisada. */
export interface IConsultaPendente {
  /** quando foi disparada (ms) — vence em `PRAZO_CONSULTA_PENDENTE_MS` */
  desde: number;
}

export interface IReacaoAoAviso {
  /** a consulta pendente a que o aviso se refere (sai das pendentes), ou `null` */
  chave: string | null;
  /** toast para quem disparou: só para consulta pendente desta tela */
  toast: "sucesso" | "erro" | null;
  /** a lista da sub-página recarrega: todo aviso da consulta dela (recorrência, lote, irmãs do CADIN) */
  recarregar: boolean;
}

/**
 * GEN-3984 — como a sub-página reage a um aviso `refreshTaxCurrentAccount`.
 *
 * - Recarrega a lista para QUALQUER aviso da consulta dela (aviso sem slug = Situação Fiscal), inclusive
 *   os que esta tela não disparou ou que chegaram depois do prazo da pendência — sem toast.
 * - Toast só para a consulta pendente desta tela (`consultaPendenteDoAviso`). Erro ou sucesso vem do
 *   `slug_status` gravado que o aviso traz (cobre o FINISH com item em erro e a irmã do CADIN coberta pelo
 *   job da raiz); sem ele (backend anterior), é sucesso — nunca falha desta consulta.
 * - A falha avisada pela fila (FAIL) já levantou o toast de erro e TIROU a pendência
 *   (`consultaQueFalhouNaFila`): o aviso de concluída que vem depois, com a linha `error`, cai no caso
 *   "não pendente" e só recarrega.
 */
export const decidirReacaoAoAviso = (
  message: IAvisoDeConsultaConcluida | null | undefined,
  {
    slug,
    pendentes,
    agora = Date.now(),
  }: { slug: string; pendentes: ReadonlyMap<string, IConsultaPendente>; agora?: number }
): IReacaoAoAviso => {
  if (!message || message.type !== "refreshTaxCurrentAccount") return { chave: null, toast: null, recarregar: false };
  const recarregar = (message.slug ?? TAX_STATUS_SLUG) === slug;
  const desdes = new Map(Array.from(pendentes.entries()).map(([chave, p]) => [chave, p.desde]));
  const chave = consultaPendenteDoAviso(message, { slug, pendentes: desdes, agora });
  if (!chave) return { chave: null, toast: null, recarregar };
  return { chave, toast: avisoTerminouComErro(message) ? "erro" : "sucesso", recarregar };
};

/**
 * GEN-3984 — a consulta pendente desta tela que a fila diz ter falhado (FAIL/FAILED), ou `null`. O painel
 * levanta o toast de erro na hora e TIRA a pendência: o aviso de concluída seguinte (linha `error`) só
 * recarrega, e um sucesso posterior da mesma empresa (recorrência, lote) não vira "sua consulta concluiu".
 */
export const consultaQueFalhouNaFila = (
  msgData: { slug?: string | null; guid_company?: string | null; status?: string | null } | null | undefined,
  pendentes: ReadonlyMap<string, IConsultaPendente>
): string | null => {
  const chave = chaveDaFalhaNaFila(msgData);
  return chave && pendentes.has(chave) ? chave : null;
};

/**
 * GEN-3984 — resposta 200 do "Atualizar consulta" que NÃO iniciou a consulta: corpo `false` (empresa
 * inativa, sem procuração/certificado, sem a credencial da consulta) ou sem corpo (`response` ausente/null —
 * o backend o omite quando o despacho ao Databroker falha). Não há aviso de concluída a esperar — sem
 * tratar, o toast de fundo ficaria aberto à toa.
 */
export const consultaNaoIniciada = (resposta: { status?: number | null; response?: unknown }): boolean =>
  resposta.status === 200 && (resposta.response === false || resposta.response == null);

/**
 * GEN-3984 — prazo do toast "Pesquisando… em segundo plano". Na Situação Fiscal, longo (10 min): a consulta
 * leva minutos e o toast fecha pelo aviso de concluída, como sempre — o prazo é só a rede para o aviso que
 * não chega. No CADIN Federal, 15 s: o aviso pode não chegar a quem disparou (a irmã coberta pela consulta
 * da raiz de outra pessoa ou da recorrência).
 */
export const PRAZO_TOAST_CADIN_EM_SEGUNDO_PLANO_MS = 15000;
export const PRAZO_TOAST_SITUACAO_FISCAL_EM_SEGUNDO_PLANO_MS = 10 * 60 * 1000;
export const prazoDoToastEmSegundoPlano = (slug: string | null | undefined): number =>
  slug === CADIN_FEDERAL_SLUG ? PRAZO_TOAST_CADIN_EM_SEGUNDO_PLANO_MS : PRAZO_TOAST_SITUACAO_FISCAL_EM_SEGUNDO_PLANO_MS;

/**
 * GEN-3984 — se a recarga SILENCIOSA da lista (por aviso de websocket) pode aplicar a resposta: só se
 * nenhuma busca do usuário (página, filtro) estiver em voo e nenhuma tiver saído desde que ela saiu
 * (`seqVisto` = a sequência das buscas do usuário quando a silenciosa partiu). A silenciosa nunca descarta
 * a busca do usuário.
 */
export const silenciosaPodeAplicar = ({
  seqVisto,
  seqAtual,
  buscasDoUsuarioEmVoo,
}: {
  seqVisto: number;
  seqAtual: number;
  buscasDoUsuarioEmVoo: number;
}): boolean => destinoDaRecargaSilenciosa({ seqVisto, seqAtual, buscasDoUsuarioEmVoo }) === "aplicar";

/**
 * GEN-3984 — o que fazer com a resposta da recarga silenciosa: aplicar; ou, se descartada, recarregar de
 * novo — depois que a busca do usuário em voo terminar (`esperar-busca`), ou já (`reagendar`, quando a
 * busca do usuário que a invalidou já voltou). O aviso pode trazer dado mais novo que a resposta do usuário.
 */
export const destinoDaRecargaSilenciosa = ({
  seqVisto,
  seqAtual,
  buscasDoUsuarioEmVoo,
  silenciosaVista = 0,
  silenciosaAtual = 0,
}: {
  seqVisto: number;
  seqAtual: number;
  buscasDoUsuarioEmVoo: number;
  /** a sequência PRÓPRIA das recargas silenciosas quando esta saiu, e a atual */
  silenciosaVista?: number;
  silenciosaAtual?: number;
}): "aplicar" | "esperar-busca" | "reagendar" | "descartar" => {
  // uma silenciosa mais nova saiu depois desta (rajada de avisos do CADIN): só ela decide e aplica
  if (silenciosaVista !== silenciosaAtual) return "descartar";
  if (buscasDoUsuarioEmVoo > 0) return "esperar-busca";
  return seqVisto === seqAtual ? "aplicar" : "reagendar";
};

const NOME_DA_JURISDICAO: Record<string, string> = { federal: "federal", state: "estadual", municipal: "municipal" };

/**
 * GEN-3984 (13/10) — o aviso do lote que não rodou, ou rodou em parte, porque o usuário não vê a
 * jurisdição da consulta. `null` quando tudo rodou (ou backend anterior, sem resumo): o lote nunca
 * fica sem retorno na tela (Thiago, 13/10).
 */
export const avisoDoLotePorJurisdicao = (
  resumo: IResumoDoLoteTaxCurrentAccount | null | undefined
): { nadaRodou: boolean; titulo: string; texto: string } | null => {
  // o texto vai para HTML (CustomAlert): só número e nomes conhecidos entram nele
  const fora = Math.floor(Number(resumo?.foraDaJurisdicao) || 0);
  if (!resumo || fora <= 0) return null;
  const nomes = [...new Set((resumo.jurisdicoesSemAcesso ?? []).map((j) => NOME_DA_JURISDICAO[j]).filter(Boolean))];
  const motivo = nomes.length
    ? `Você não tem acesso ${nomes.length > 1 ? "às jurisdições" : "à jurisdição"} ${
        nomes.length > 1 ? `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}` : nomes[0]
      }.`
    : "Você não tem acesso à jurisdição dessas consultas.";
  // "nada rodou" só quando o backend diz que nenhuma entrou no lote; sem o número, o comportamento de antes.
  // No parcial, o aviso só fala das recusadas: o que o lote fez com as aceitas aparece no painel do lote.
  const nadaRodou = typeof resumo.aceitos === "number" && resumo.aceitos === 0;
  return nadaRodou
    ? { nadaRodou, titulo: "Nenhuma consulta foi iniciada.", texto: motivo }
    : {
        nadaRodou,
        titulo: `${fora} ${fora === 1 ? "consulta não foi iniciada" : "consultas não foram iniciadas"}.`,
        texto: motivo,
      };
};

/** GEN-3984 (13/10) — o filtro de Certidões que apontava para o CADIN Federal antes da migração. */
export const ehLinkAntigoDoCadinFederal = (search: string | null | undefined): boolean =>
  new URLSearchParams(search ?? "").get("type") === CADIN_FEDERAL_SLUG;

/**
 * GEN-3984 — as linhas que o lote pode pedir: a que traz `motivoSemConsulta` (CADIN de empresa inativa,
 * produto desligado, cliente sem gov.br) fica de fora, com a mesma régua do botão da linha.
 */
export const separarLinhasDoLote = <T extends { motivoSemConsulta?: string | null }>(linhas: ReadonlyArray<T>) => ({
  elegiveis: linhas.filter((linha) => !linha.motivoSemConsulta),
  semConsulta: linhas.filter((linha) => !!linha.motivoSemConsulta).length,
});

/** GEN-3984 — o aviso das linhas que o lote deixou de fora por pré-requisito (texto fixo: nada do servidor vai ao HTML). */
export const avisoDoLotePorPreRequisito = (semConsulta: number, nenhumaElegivel: boolean) =>
  semConsulta <= 0
    ? null
    : {
        titulo: nenhumaElegivel
          ? "Nenhuma consulta foi iniciada."
          : `${semConsulta} ${semConsulta === 1 ? "consulta não foi iniciada" : "consultas não foram iniciadas"}.`,
        texto: "Não atendem aos pré-requisitos da consulta. O motivo aparece no botão de cada linha.",
      };
